import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { XIcon } from "@heroicons/react/outline";
import { projectsAPI } from "../../api/projects";
import { usersAPI } from "../../api/users";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";
import toast from "react-hot-toast";

const AssignUserModal = ({ projectId, onClose, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_id: "",
    role: "sub_manager",
  });
  const queryClient = useQueryClient();

  // Fetch all users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => usersAPI.getAll(),
  });

  // Fetch existing assignments for this project
  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery({
    queryKey: ["project-assignments", projectId],
    queryFn: () => projectsAPI.getAssignments(projectId),
    enabled: !!projectId,
  });

  const assignMutation = useMutation({
    mutationFn: (data) => {
      console.log("📝 Assigning user:", data);
      return projectsAPI.assignUser(projectId, data);
    },
    onSuccess: () => {
      toast.success("User assigned successfully");
      queryClient.invalidateQueries(["project-assignments", projectId]);
      queryClient.invalidateQueries(["projects"]);
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      const message =
        error.response?.data?.error?.message || "Failed to assign user";
      toast.error(message);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.user_id) {
      toast.error("Please select a user");
      return;
    }
    setLoading(true);
    assignMutation.mutate(formData);
    setLoading(false);
  };

  if (usersLoading || assignmentsLoading) return <LoadingSpinner />;

  const allUsers = usersData?.data || [];
  const assignments = assignmentsData?.data || [];

  // Get IDs of already assigned users
  const assignedUserIds = assignments.map((a) => a.user_id);

  // Filter users:
  // 1. Remove Super Admins (they shouldn't be assigned to projects)
  // 2. Remove already assigned users
  // 3. Remove current user (can't assign yourself)
  // 4. Remove duplicates (by id)
  const availableUsers = allUsers
    .filter((u) => u.role !== "super_admin") // ← EXCLUDE Super Admins
    .filter((u) => u.id !== currentUser?.id) // ← EXCLUDE current user
    .filter((u) => !assignedUserIds.includes(u.id)) // ← EXCLUDE already assigned
    .filter(
      (u, index, self) => self.findIndex((t) => t.id === u.id) === index, // ← Remove duplicates
    );

  // Group users by role for better UX
  const groupedUsers = {
    project_manager: availableUsers.filter((u) => u.role === "project_manager"),
    sub_manager: availableUsers.filter((u) => u.role === "sub_manager"),
    foreman: availableUsers.filter((u) => u.role === "foreman"),
    worker: availableUsers.filter((u) => u.role === "worker"),
  };

  // Get role icon
  const getRoleIcon = (role) => {
    const icons = {
      project_manager: "👔",
      sub_manager: "📋",
      foreman: "🔧",
      worker: "👷",
    };
    return icons[role] || "👤";
  };

  // Get role color for badge
  const getRoleBadgeColor = (role) => {
    const colors = {
      project_manager: "badge-primary",
      sub_manager: "badge-info",
      foreman: "badge-warning",
      worker: "badge-success",
    };
    return colors[role] || "badge-info";
  };

  // Count total available
  const totalAvailable = availableUsers.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Assign User to Project
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {totalAvailable} users available for assignment
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <XIcon className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Select User *
            </label>
            <select
              required
              value={formData.user_id}
              onChange={(e) =>
                setFormData({ ...formData, user_id: e.target.value })
              }
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a user</option>

              {/* Project Managers */}
              {groupedUsers.project_manager.length > 0 && (
                <optgroup label="👔 Project Managers">
                  {groupedUsers.project_manager.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </optgroup>
              )}

              {/* Sub Managers */}
              {groupedUsers.sub_manager.length > 0 && (
                <optgroup label="📋 Sub Managers">
                  {groupedUsers.sub_manager.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </optgroup>
              )}

              {/* Foremen */}
              {groupedUsers.foreman.length > 0 && (
                <optgroup label="🔧 Foremen">
                  {groupedUsers.foreman.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </optgroup>
              )}

              {/* Workers */}
              {groupedUsers.worker.length > 0 && (
                <optgroup label="👷 Workers">
                  {groupedUsers.worker.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {totalAvailable === 0 && (
              <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">
                ⚠️ No users available. All users are either already assigned or
                are Super Admins.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Role on Project
            </label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            >
              <option value="project_manager">👔 Project Manager</option>
              <option value="sub_manager">📋 Sub Manager</option>
              <option value="foreman">🔧 Foreman</option>
              <option value="worker">👷 Worker</option>
            </select>
          </div>

          {/* Show selected user info */}
          {formData.user_id && (
            <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Selected User
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-white">
                {availableUsers.find((u) => u.id === parseInt(formData.user_id))
                  ?.full_name || "Unknown"}
              </p>
              <span
                className={`badge ${getRoleBadgeColor(
                  availableUsers.find(
                    (u) => u.id === parseInt(formData.user_id),
                  )?.role,
                )} mt-1`}
              >
                {getRoleIcon(
                  availableUsers.find(
                    (u) => u.id === parseInt(formData.user_id),
                  )?.role,
                )}{" "}
                {availableUsers.find((u) => u.id === parseInt(formData.user_id))
                  ?.role || "Unknown"}
              </span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading || assignMutation.isLoading || totalAvailable === 0
              }
              className="btn-primary"
            >
              {loading || assignMutation.isLoading
                ? "Assigning..."
                : "Assign User"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AssignUserModal;
